// Consumes Supabase implicit-grant magic-link tokens from the URL hash.
//
// The shared client runs flowType 'pkce' (supabase.js), and supabase-js
// REFUSES to parse an implicit-grant hash under a PKCE client ("Not a valid
// PKCE flow url") — so #access_token from an admin-generated magic link must
// be consumed manually. Historically only the game's /welcome page did this;
// a magic link that redirected anywhere else (the login modal used
// `/?code=XXXX`, the web app uses /redeem) left the user signed out with live
// tokens sitting in the address bar (TAS-166).
//
// Call once on app/page mount. Idempotent: the tokens are stripped from the
// URL IMMEDIATELY (before setSession resolves) so they never linger in
// history and a second call finds nothing. Query params (?code= etc.) survive.
import { supabase } from './supabase';
import { takeAuthFragment } from './claimHandoff.js';

function stripHash() {
  try {
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  } catch {
    /* history API unavailable — noop */
  }
}

/**
 * @returns {Promise<{attempted: boolean, error: string|null}>}
 *   attempted=false when the URL carried no magic-link hash.
 */
export async function consumeMagicLinkHash() {
  if (typeof window === 'undefined' || !window.location) {
    return { attempted: false, error: null };
  }
  // analytics-logger moves the fragment off the URL at module load, BEFORE
  // mixpanel.init() — otherwise Mixpanel's own $current_url carries the
  // tokens on every event. So the stash is the primary source; the URL is the
  // fallback for any entry point that loads no analytics at all.
  const hash = takeAuthFragment() || window.location.hash?.replace(/^#/, '');
  if (!hash) return { attempted: false, error: null };
  const params = new URLSearchParams(hash);

  const errParam = params.get('error_description') || params.get('error');
  const access_token = params.get('access_token');
  const refresh_token = params.get('refresh_token');

  if (errParam && !access_token) {
    stripHash();
    return { attempted: true, error: decodeURIComponent(errParam.replace(/\+/g, ' ')) };
  }
  if (!access_token || !refresh_token) return { attempted: false, error: null };

  stripHash();
  try {
    const { error } = await supabase.auth.setSession({ access_token, refresh_token });
    return { attempted: true, error: error ? error.message : null };
  } catch (e) {
    return { attempted: true, error: e?.message || 'setSession failed' };
  }
}
